import { type ProgramGuideItem, type OnDemandItem, PlayItem, type  Channel } from '@ca.vmedia.tv/domains';
import { OnDemandCardData, MixedCardData, ProgramCardData, ChannelLiveCardData, type ShowAllCardData } from "@/components/Cards/types";
import { AppConstants } from "@/common";
import { useExtraCards } from "@/composables/useExtraCards";
import { useHomePageStore } from "@/stores/HomePage";
import { useChannelsStore } from "@/stores/Channels";
import type { ExposeInterface } from './types';

export const useLinesHomeStore = defineStore('LinesHome', (): ExposeInterface => {
    const ChannelsStore = useChannelsStore();
    const {
        getAvailableChannels: allChannels,
    } = storeToRefs(ChannelsStore);

    const HomePageStoreModule = useHomePageStore();
    const {
        getMovies,
        getSports,
        getWatchAgain,
        getRecentlyOnDemand,
        getOlympics,
        getKids,
        getContinueWatching,
        getNews,
        getRecentChannels,
        getTrends,
        getLiveSeries,
    } = storeToRefs(HomePageStoreModule);


    const { hasShowAll, getCardShowAll } = useExtraCards();

    const sortPrograms = (items: ProgramGuideItem[]): ProgramGuideItem[] => {
        const accumulator = {cardLive: [], cardVCloud: [], cardOther: []};
        const reducer = (acc: {[key: string]: ProgramGuideItem[]}, item: ProgramGuideItem) => {
            const currentChannel = allChannels.value.find((channel: Channel) => channel.id === item?.recentAirTime?.channelId) ?? null;
            if (PlayItem.isLive(item)) {
                acc.cardLive.push(item);
            } else if (PlayItem.isVCloud(item, currentChannel)) {
                acc.cardVCloud.push(item);
            } else {
                acc.cardOther.push(item);
            }
            return acc;
        };
        const reducedCards = items.reduce(reducer, accumulator);
        return [...reducedCards.cardLive, ...reducedCards.cardVCloud, ...reducedCards.cardOther];
    };

    const filteredOnDemand = (items: OnDemandItem[]): OnDemandItem[] => items.filter((show) => show.id !== null);

    const getCardsRecentChannels = computed((): ChannelLiveCardData[] | null  => {
        if (getRecentChannels.value === null) {
            return null;
        }
        return getRecentChannels.value.map((item) => new ChannelLiveCardData(item));
    });

    const getCardsOlympics = computed((): (ProgramCardData | ShowAllCardData)[] | null => {
        const data = getOlympics.value

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = sortPrograms(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new ProgramCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsMovies = computed((): (ProgramCardData | ShowAllCardData)[] | null => {
        const data = getMovies.value

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = sortPrograms(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new ProgramCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsSports = computed((): (ProgramCardData | ShowAllCardData)[] | null => {
        const data = getSports.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = sortPrograms(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new ProgramCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsKids = computed((): (ProgramCardData | ShowAllCardData)[] | null => {
        const data = getKids.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = sortPrograms(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new ProgramCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsNews = computed((): (ProgramCardData | ShowAllCardData)[] | null => {
        const data = getNews.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = sortPrograms(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new ProgramCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsContinueWatching = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getContinueWatching.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = filteredOnDemand(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    }); 


    const getCardsRecentlyOnDemand = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getRecentlyOnDemand.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = filteredOnDemand(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    }); 

    const getCardsWatchAgain = computed((): (OnDemandCardData | ShowAllCardData)[] | null => {
        const data = getWatchAgain.value;

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = filteredOnDemand(data.items).slice(0, AppConstants.HomeItemsInLine).map((item) => new OnDemandCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll<OnDemandCardData>(items, data.paging)];
        }
        
        return items;
    }); 

    const getCardsTrends = computed((): (MixedCardData | ShowAllCardData)[] | null => {
        const data = getTrends.value

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.slice(0, AppConstants.HomeItemsInLine).map((item) => new MixedCardData(item));
        
        if (hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }
        
        return items;
    });

    const getCardsLiveSeries = computed((): (ProgramCardData | ShowAllCardData)[] | null => {
        const data = getLiveSeries.value

        if (data === null || data.items === null) {
            return null;
        }

        if (data.items.length === 0) {
            return [];
        }

        const items = data.items.slice(0, AppConstants.HomeItemsInLine).map((item) => new ProgramCardData(item));

        if (data.paging !== null && hasShowAll(data.paging)) {
            return [...items, ...getCardShowAll(items, data.paging)];
        }

        return items;
    });


    const _counterRows: Ref<number> = ref(AppConstants.HomeCounterLines);
    const getCounterLinesHome= computed((): number => _counterRows.value);
    const _clearCounter = (): void => {
        _counterRows.value = AppConstants.HomeCounterLines;
    };
    const increaseCounterLinesHome = (): void => {
        _counterRows.value += 1;
    };

    const clearAllLinesHome = (): void => {
        _clearCounter();
    };

    return {
        getCardsWatchAgain,
        getCardsTrends,
        getCardsRecentlyOnDemand,
        getCardsContinueWatching,
        getCardsNews,
        getCardsKids,
        getCardsMovies,
        getCardsSports,
        getCardsOlympics,
        getCardsRecentChannels,
        clearAllLinesHome,
        increaseCounterLinesHome,
        getCounterLinesHome,
        getCardsLiveSeries
    };

});